"use client";

import { Clock, MapPin, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePhysicalHub() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1105" id="the-physical-hub" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28">
      {}
      <svg className="pointer-events-none absolute top-12 right-[8%] hidden md:block" width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
        <path d="M22 4 L25 18 L39 21 L25 24 L22 38 L19 24 L5 21 L19 18 Z" stroke="var(--primary)" strokeWidth="3" strokeLinejoin="round" fill="none" />
      </svg>
      <svg className="pointer-events-none absolute bottom-16 left-[6%] hidden md:block" width="56" height="40" viewBox="0 0 56 40" fill="none" aria-hidden="true">
        <path d="M4 30 Q14 6, 28 20 T52 12" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" />
      </svg>
      <svg className="pointer-events-none absolute top-1/2 right-[3%] hidden lg:block" width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <circle cx="18" cy="18" r="6" stroke="var(--primary)" strokeWidth="3" fill="none" />
        <path d="M18 2 L18 8 M18 28 L18 34 M2 18 L8 18 M28 18 L34 18" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" />
      </svg>

      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-[1320px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {}
          <div ref={leftRef} className="lg:col-span-5 transition-all duration-700 ease-out" style={{
          transform: leftVisible ? "translateY(0)" : "translateY(24px)",
          opacity: leftVisible ? 1 : 0,
          transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        }}>
            {}
            <div className="flex items-center gap-3 mb-4">
              <span className="font-default text-[14px] font-black uppercase tracking-[0.18em] text-primary">LOCATION</span>
            </div>

            {}
            <svg className="block mb-8 w-full max-w-[280px]" height="10" viewBox="0 0 280 10" fill="none" aria-hidden="true" preserveAspectRatio="none">
              <path d="M2 5 Q 17.5 -2, 35 5 T 70 5 T 105 5 T 140 5 T 175 5 T 210 5 T 245 5 T 278 5" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" fill="none" />
            </svg>

            {}
            <h2 className="font-default text-foreground font-extrabold leading-[0.95] tracking-tight text-[clamp(2.25rem,5.5vw,5rem)] mb-6">
              VISIT THE{" "}
              <span className="relative inline-block">
                <span className="relative z-10">COMMAND</span>
                <svg className="absolute left-1/2 top-1/2 -z-0 pointer-events-none" style={{
                transform: "translate(-50%, -50%) rotate(-3deg)",
                width: "118%",
                height: "150%"
              }} viewBox="0 0 220 90" fill="none" aria-hidden="true" preserveAspectRatio="none">
                  <path d="M110 8 C 50 8, 14 22, 14 45 C 14 68, 60 82, 112 82 C 168 82, 208 66, 206 44 C 204 22, 162 10, 108 10" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" fill="none" strokeDasharray="600" strokeDashoffset={leftVisible ? 0 : 600} style={{
                  transition: "stroke-dashoffset 900ms ease-out 200ms"
                }} />
                </svg>
              </span>
            </h2>

            <p className="font-default text-foreground/85 text-[clamp(1.0625rem,1.2vw,1.25rem)] font-medium leading-[1.55] mb-8 max-w-[480px]">
              Code Hub is the central node for builders. Join us at our physical lab for weekly workshops, hardware hacking, and collaborative project nights. Check the map below to find our headquarters.
            </p>

            {}
            <div className="mb-10 inline-block">
              <div className="relative inline-flex items-center gap-2 bg-background text-foreground border-[4px] border-foreground px-5 py-3 rounded-lg" style={{
              transform: "rotate(4deg)",
              boxShadow: "8px 8px 0 0 var(--primary)"
            }}>
                <Clock className="size-5" strokeWidth={3} />
                <span className="font-default text-lg font-extrabold tracking-tight">
                  OPEN DAILY 10A–10P
                </span>
              </div>
            </div>

            {}
            <div>
              <Button asChild size="lg" className="group relative bg-primary text-primary-foreground border-[3px] border-foreground rounded-lg px-8 py-6 h-auto font-black uppercase tracking-wide text-base hover:bg-primary hover:-translate-x-[3px] hover:-translate-y-[3px] transition-transform duration-200 ease-out" style={{
              boxShadow: "0 0 0 0 var(--foreground)"
            }}>
                <Link to="/JoinUsPage" className="inline-flex items-center gap-2" onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = "6px 6px 0 0 var(--foreground)";
              }} onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = "0 0 0 0 var(--foreground)";
              }}>
                  <Navigation className="size-5" strokeWidth={3} />
                  GET DIRECTIONS
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7 relative transition-all duration-700 ease-out" style={{
          transform: rightVisible ? "translateY(0)" : "translateY(24px)",
          opacity: rightVisible ? 1 : 0,
          transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          transitionDelay: "120ms"
        }}>
            {}
            <div className="absolute -top-4 -left-3 z-30 h-8 w-24 bg-primary/70 pointer-events-none" style={{
            transform: "rotate(-14deg)"
          }} aria-hidden="true" />

            {}
            <svg className="absolute -top-3 -right-3 z-20 pointer-events-none" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
              <path d="M6 24 Q 14 4, 24 14 T 42 8" stroke="var(--background)" strokeWidth="4" strokeLinecap="round" fill="none" />
            </svg>
            <svg className="absolute -bottom-3 -left-3 z-20 pointer-events-none" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
              <path d="M4 22 Q 16 38, 26 26 T 44 32" stroke="var(--background)" strokeWidth="4" strokeLinecap="round" fill="none" />
            </svg>
            <svg className="absolute -bottom-3 -right-3 z-20 pointer-events-none" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
              <path d="M40 6 Q 22 14, 28 26 T 8 38" stroke="var(--background)" strokeWidth="4" strokeLinecap="round" fill="none" />
            </svg>

            {}
            <div className="relative rounded-lg overflow-hidden border-[4px] border-background bg-foreground/40" style={{
            boxShadow: "12px 12px 0 0 var(--primary)"
          }}>
              <div className="relative aspect-[7/5] w-full">
                <iframe title="Code Hub location" src="https://www.google.com/maps?q=Tech+District&t=&z=13&ie=UTF8&iwloc=&output=embed" className="absolute inset-0 h-full w-full" style={{
                border: 0,
                filter: "grayscale(0.4) contrast(1.05) hue-rotate(280deg) saturate(0.7) brightness(0.85)"
              }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />

                {}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
                  <div className="relative flex items-center justify-center">
                    {}
                    <span className="absolute inline-flex h-24 w-24 rounded-lg bg-primary/40" style={{
                    animation: "gw-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite"
                  }} aria-hidden="true" />
                    <span className="absolute inline-flex h-16 w-16 rounded-lg bg-primary/60" style={{
                    animation: "gw-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite",
                    animationDelay: "0.4s"
                  }} aria-hidden="true" />
                    {}
                    <div className="relative z-10 flex items-center justify-center bg-primary border-[3px] border-background rounded-lg px-3 py-2" style={{
                    boxShadow: "4px 4px 0 0 var(--foreground)"
                  }}>
                      <MapPin className="size-7 text-primary-foreground" strokeWidth={3} fill="currentColor" />
                    </div>
                  </div>
                </div>

                {}
                <div className="absolute bottom-4 left-4 z-20 inline-flex items-center gap-2 bg-background text-foreground border-[3px] border-background/0 px-4 py-2 rounded-lg" style={{
                transform: "rotate(-3deg)",
                boxShadow: "4px 4px 0 0 var(--primary)"
              }}>
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75" style={{
                    animation: "gw-ping 1.4s cubic-bezier(0, 0, 0.2, 1) infinite"
                  }} />
                    <span className="relative inline-flex h-3 w-3 rounded-lg bg-primary" />
                  </span>
                  <span className="font-default text-xs font-black uppercase tracking-[0.12em]">
                    Active · Tech Hub
                  </span>
                </div>
              </div>
            </div>

            {}
            <p className="font-default text-primary text-2xl mt-6 ml-4" style={{
            transform: "rotate(-2deg)"
          }}>
              build together — join us!
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gw-ping {
          0% {
            transform: scale(0.6);
            opacity: 0.9;
          }
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </section>;
}